import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import Typography from '@material-ui/core/Typography';
import InputLabel from '@material-ui/core/InputLabel';
import Drawer from '@material-ui/core/Drawer';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import CircularProgress from '@material-ui/core/CircularProgress';
import Tooltip from '@material-ui/core/Tooltip';

import area from '@turf/area';

import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import logo from './img/lcc_logo.png';

const useStyles = makeStyles(theme => ({
  heading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingRight: theme.spacing(4)
  },
  logo: {
    height: 75,
    margin: theme.spacing(1)
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '500px'
  },
  label: {
    padding: theme.spacing(0.5)
  },
  listLabel: {
    paddingTop: theme.spacing(1),
    alignSelf: 'left'
  },
  textField: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    marginTop: theme.spacing(0.5),
    flexShrink: 0,
    width: 150,
    '& .MuiOutlinedInput-input': {
      padding: theme.spacing(1)
    }
  },
  numberField: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    marginTop: theme.spacing(0.5),
    flexShrink: 0,
    width: 90,
    '& .MuiOutlinedInput-input': {
      padding: theme.spacing(1)
    }
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
    // marginLeft: theme.spacing(1)
  },
  panelHeading: {
    fontSize: theme.typography.pxToRem(15)
  },
  group: {
    display: 'flex',
    flexDirection: 'column'
  },
  button: {
    margin: theme.spacing(1),
    maxWidth: '300px',
    alignSelf: 'center'
  },
  closeButton: {
    padding: theme.spacing(0.5)
  },
  addressRow: {
    justifyContent: 'space-between'
  },
  errMsg: {
    color: 'red',
    marginLeft: theme.spacing(1),
    alignSelf: 'center'
  },
  divider: {
    marginTop: theme.spacing(1)
  },
  buttonProgress: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12
  }
}));

const categories = {
  'Development Type': [
    'Subdivision',
    'Other',
    'Aged & Disabled Housing',
    'Child Care',
    'Retail / Business',
    'School',
    'Church'
  ]
};

const TooltipContent = {
  Location: (
    <>
      <Typography variant='body2'>
        Select one or more properties from the map, or search for an address
        using the search bar.
        <br />
        <b>{'Tip'}</b>: click on a selected property to unselect.
      </Typography>
    </>
  ),
  'Existing dwellings': (
    <>
      <Typography variant='body2'>
        A credit is generally applied for the existing dwelling/s on the site.
      </Typography>
    </>
  ),
  Subdivision: (
    <>
      <Typography variant='body2'>
        Include the total number of lots that will be created at the completion
        of development of the site.
      </Typography>
    </>
  )
};

const SideBar = ({ formData, setFormData, removeLot }) => {
  const classes = useStyles();
  // TODO import from ../../server/sheetInputs
  const [outTable, setOutTable] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);

  let fileName;
  const runSheet = () => {
    setIsLoading(true);
    const landArea = formData['Location']['Selected Lots'].reduce(
      (prev, lot) => prev + area(lot),
      0
    );
    fetch('/calculate', {
      method: 'POST',
      body: JSON.stringify({
        ...Object.values(formData).reduce(
          (prev, current) => ({ ...prev, ...current }),
          {}
        ),
        'Land area': Math.round(landArea / 10) * 10
      }),
      headers: {
        'Content-Type': 'application/json; charset=utf-8'
      }
    })
      .then(response => {
        fileName = response.headers
          .get('content-disposition')
          .match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)[1];
        return response.arrayBuffer();
      })
      .then(resp => {
        const file = new Blob([resp], { type: 'application/pdf' });
        // process to auto download it
        const fileURL = URL.createObjectURL(file);
        const link = document.createElement('a');
        link.href = fileURL;
        link.download = fileName;
        link.click();
        setIsLoading(false);
      })
      .catch(e => {
        setIsLoading(false);
        alert('Error processing form');
        console.log(e);
      });
  };

  // validation
  // let message = '';
  // const arrSum = arr => arr.reduce((a, b) => a + b, 0);
  // if (arrSum(Object.values(formData['Dwellings'])) == 0)
  //   message = 'Dwellings cannot all be 0.';

  const updateFormData = (title, name, value) => {
    if (title === 'Subdivision' && name === 'Number of Proposed Lots' && value < 2) {
      value = 2
    }
    setFormData({
      ...formData,
      [title]: {
        ...formData[title],
        [name]: value
      }
    })
  }


  return (
    <div>
      <Drawer
        className={classes.drawer}
        anchor='left'
        open
        variant='persistent'
        classes={{
          paper: classes.drawerPaper
        }}
      >
        <div className={classes.heading}>
          <img className={classes.logo} src={logo} alt='Logo' />
          <Typography variant='h5'>Contribution Calculator</Typography>
        </div>

        <form className={classes.container} noValidate autoComplete='off'>
          {Object.keys(formData)
            .filter(k => k !== 'Dwelling density') //Dwelling density isn't set manually
            .map(title => {
              const inputs = formData[title];
              return (
                <Tooltip key={title} placement="right" enterDelay={500} leaveDelay={200} title={TooltipContent[title]} >
                  <div>
                    <ExpansionPanelSummary>
                      <Typography className={classes.panelHeading}>
                        {title}
                      </Typography>
                    </ExpansionPanelSummary>
                    <ExpansionPanelDetails className={classes.group}>
                      {Object.keys(inputs).map(name => {
                        // Show list of selected lots
                        if (name === 'Selected Lots') {
                          return (
                            <React.Fragment key={name}>
                              <InputLabel className={classes.listLabel}>
                                {name}:
                              </InputLabel>
                              <List dense>
                                {inputs[name].map(lot => (
                                  <ListItem
                                    key={lot.properties.GlobalID}
                                    className={classes.addressRow}
                                  >
                                    <Typography variant='body2'>
                                      {lot.properties.Address}
                                    </Typography>
                                    <IconButton
                                      className={classes.closeButton}
                                      onClick={() => removeLot(lot)}
                                    >
                                      <CloseIcon />
                                    </IconButton>
                                  </ListItem>
                                ))}
                              </List>
                            </React.Fragment>
                          );
                        }

                        let dtype = typeof inputs[name] || 'string';
                        if (dtype === 'string' && inputs[name] !== '') {
                          if (!isNaN(Number(inputs[name]))) {
                            inputs[name] = Number(inputs[name]);
                            dtype = 'number';
                          }
                        }
                        // if the name is a category, render a select
                        if (categories && categories[name]) {
                          const options = [
                            ...new Set(categories[name].concat([inputs[name]]))
                          ];
                          return (
                            <div className={classes.row} key={name}>
                              <InputLabel className={classes.label}>
                                {name}
                              </InputLabel>
                              <TextField
                                key={name}
                                variant='outlined'
                                select
                                className={classes.textField}
                                value={inputs[name]}
                                onChange={evt => updateFormData(title, name, evt.target.value)}
                              >
                                {options.map(option => (
                                  <MenuItem key={name + option} value={option}>
                                    {option}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </div>
                          );
                        }
                        // otherwise render normal text field
                        return (
                          <div className={classes.row} key={name}>
                            <InputLabel className={classes.label}>
                              {name}
                            </InputLabel>
                            <TextField
                              key={name}
                              variant='outlined'
                              className={
                                dtype === 'number'
                                  ? classes.numberField
                                  : classes.textField
                              }
                              value={inputs[name]}
                              type={dtype}
                              onChange={evt => updateFormData(title, name, evt.target.value)}
                            />
                          </div>
                        );
                      })}
                    </ExpansionPanelDetails>
                  </div>
                </Tooltip>
              );
            })}
        </form>
        <Tooltip
          placement="bottom"
          enterDelay={500}
          leaveDelay={200}
          title={
            <>
              <Typography variant='body2'>
                To calculate the contribution, please select a property, detail
                the number of existing dwellings and the number of lots that
                will be created.
              </Typography>
            </>
          }
        >
          <div className={classes.button}>
            <Button
              onClick={evt => runSheet()}
              variant='contained'
              color='primary'
              className={classes.button}
              disabled={
                isLoading || formData['Location']['Selected Lots'].length === 0
              }
            >
              Calculate Contributions
              {isLoading && (
                <CircularProgress
                  size={24}
                  className={classes.buttonProgress}
                />
              )}
            </Button>
          </div>
        </Tooltip>
      </Drawer>
      <Dialog
        onClose={() => setOutTable('')}
        aria-labelledby='simple-dialog-title'
        open={outTable !== ''}
      >
        <DialogTitle id='simple-dialog-title'> Contributions </DialogTitle>
        <div
          dangerouslySetInnerHTML={{
            __html: (() => outTable)()
          }}
        />
      </Dialog>
    </div>
  );
};

export default SideBar;
